import { useEffect, useRef } from "react"

import { onRecordStatusSignal } from "../helpers/automatic-move-events.ts"

/** Several notices about one record (a move and its suggestion) read once. */
export const AUTOMATIC_MOVE_DEBOUNCE_MS = 500
/** Long enough for the server's rule to have run after the write. */
export const EXPECTED_MOVE_RECHECK_MS = 3000

/**
 * Re-reads the open record when a status rule may have moved it: soon after a
 * notification names it, and once a few seconds after a write on this page that
 * commonly sets a rule off. Only one re-read is ever pending.
 */
export const useRefreshAfterAutomaticMove = (
  recordId: string | null | undefined,
  refresh: () => void
) => {
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    if (!recordId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let dueAt = Infinity

    const schedule = (delay: number) => {
      const at = Date.now() + delay
      // A sooner re-read already covers a later one.
      if (timer && dueAt <= at) return
      if (timer) clearTimeout(timer)
      dueAt = at
      timer = setTimeout(() => {
        timer = null
        dueAt = Infinity
        refreshRef.current()
      }, delay)
    }

    const unsubscribe = onRecordStatusSignal((signal) => {
      if (signal.kind === "expected") {
        schedule(EXPECTED_MOVE_RECHECK_MS)
        return
      }
      if (signal.recordIds.includes(recordId)) {
        // A notice replaces whatever was pending with its own short wait.
        if (timer) clearTimeout(timer)
        timer = null
        dueAt = Infinity
        schedule(AUTOMATIC_MOVE_DEBOUNCE_MS)
      }
    })

    return () => {
      unsubscribe()
      if (timer) clearTimeout(timer)
    }
  }, [recordId])
}
